import React from "react"

import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import AutoSizer from "react-virtualized-auto-sizer"
import { FixedSizeGrid } from "react-window"
import { ProductCatalogItem } from "~/src/serializedRecords/productCatalogItem"
import { CatalogProductTile } from "./CatalogProductTile"

//region Types
export type CatalogItemGridProps = {
  items: ProductCatalogItem[]
  onLoveProduct?: (item: ProductCatalogItem, index: number) => void
  onHideProduct?: (item: ProductCatalogItem, index: number) => void
  onAddToProjectRequest?: (item: ProductCatalogItem, index: number) => void
}
//endregion

//region Component
export function CatalogItemGrid(props: CatalogItemGridProps) {
  const { items, onLoveProduct, onHideProduct, onAddToProjectRequest } = props
  // `baseWidth` is used as the "attempted" width of each column in the grid.
  const baseWidth = 230
  const minColumnCount = 2
  const itemCount = items.length

  return (
    <>
      {itemCount === 0 ? (
        <div className="flex justify-center h-full">
          <EmptyItemGrid />
        </div>
      ) : (
        <AutoSizer>
          {({ height, width }) => {
            const columnCount = Math.max(minColumnCount, Math.floor(width / baseWidth))
            const rowCount = Math.ceil(itemCount / columnCount)
            // `columnWidth` is the actual width of each column in the grid which boils down to the
            // `baseWidth` plus left over white space.
            const columnWidth = width / columnCount

            return (
              <FixedSizeGrid
                columnCount={columnCount}
                columnWidth={columnWidth}
                rowCount={rowCount}
                rowHeight={365}
                height={height}
                width={width}
              >
                {({ columnIndex, rowIndex, style }) => {
                  const index = rowIndex * columnCount + columnIndex
                  const item = items[index]

                  if (item == null) return <></>

                  const dataProps = {}
                  // Adds data attributes to work with Tailwind by marking the
                  // first item, last item, and the first and last items in each row.
                  if (columnIndex === 0) dataProps["data-row-start"] = ""
                  if (columnIndex === columnCount - 1) dataProps["data-row-end"] = ""
                  if (index === 0) dataProps["data-start"] = ""
                  if (index === itemCount - 1) dataProps["data-end"] = ""

                  const { productId: id, ...rest } = item
                  return (
                    <div key={id} {...dataProps} className="flex items-center justify-center p-1" style={style}>
                      <CatalogProductTile
                        id={id}
                        onLove={() => onLoveProduct?.(item, index)}
                        onHide={() => onHideProduct?.(item, index)}
                        onAddToProjectRequest={() => onAddToProjectRequest?.(item, index)}
                        {...rest}
                      />
                    </div>
                  )
                }}
              </FixedSizeGrid>
            )
          }}
        </AutoSizer>
      )}
    </>
  )
}
//endregion

function EmptyItemGrid() {
  return (
    <div className="flex flex-col justify-center items-center p-8">
      <MagnifyingGlassIcon className="h-14 w-14 text-gray-300" />
      <h1 className="text-base font-medium leading-6 text-center pt-4">We couldn’t find what you were looking for</h1>
      <p className="text-sm text-center">Make sure all words are spelled correctly or try different search terms.</p>
    </div>
  )
}
